import { useState } from 'react';
import { Box, Button, CircularProgress, Snackbar, Alert } from '@mui/material';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import StyleIcon from '@mui/icons-material/Style';
import { useMutation } from '@tanstack/react-query';
import { locationsApi } from '../../api/services';
import { extractApiError } from '../../utils/apiErrors';

type Piece = 'sheet' | 'cards';

interface Props {
  locationId: number | string;
}

/**
 * The two onboarding print-kit downloads [sc-6305], shared by the Go Live detail
 * page and the Location Dashboard's Location Configuration section so both
 * surfaces offer byte-identical documents.
 *
 * Callers gate visibility (internal locations get no kit — their public report
 * page 404s, so the QR would be dead); this component only owns the downloads.
 */
export default function OnboardingKitDownloads({ locationId }: Props) {
  const [snackbar, setSnackbar] = useState<{ severity: 'success' | 'error'; message: string } | null>(null);

  const download = useMutation({
    mutationFn: (piece: Piece) => locationsApi.onboardingKitPdf(locationId, piece),
    onSuccess: ({ blob, filename }) => {
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      // Defer revoke: revoking synchronously right after click can cancel the
      // in-flight download on some browsers.
      setTimeout(() => URL.revokeObjectURL(url), 0);
      setSnackbar({ severity: 'success', message: 'PDF downloaded.' });
    },
    // The request is responseType:'blob', so on error the server's JSON {error}
    // arrives as a Blob — read it back before falling through to the generic message.
    onError: async (e) => {
      let message = 'Onboarding kit download failed';
      const data = (e as { response?: { data?: unknown } })?.response?.data;
      if (data instanceof Blob) {
        try {
          const parsed = JSON.parse(await data.text());
          // Type-checked before it becomes a React child, matching extractApiError.
          if (typeof parsed?.error === 'string') message = parsed.error;
        } catch { /* keep default */ }
      } else {
        message = extractApiError(e, message);
      }
      setSnackbar({ severity: 'error', message });
    },
  });

  // useMutation already tracks in-flight state and which piece was requested.
  const pending: Piece | null = download.isPending ? download.variables ?? null : null;

  return (
    <Box>
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
        <Button
          variant="outlined"
          size="small"
          startIcon={pending === 'sheet' ? <CircularProgress size={16} /> : <PictureAsPdfIcon />}
          disabled={pending !== null}
          onClick={() => download.mutate('sheet')}
        >
          Dashboard Sheet
        </Button>
        <Button
          variant="outlined"
          size="small"
          startIcon={pending === 'cards' ? <CircularProgress size={16} /> : <StyleIcon />}
          disabled={pending !== null}
          onClick={() => download.mutate('cards')}
        >
          Notecards
        </Button>
      </Box>
      <Snackbar
        open={snackbar !== null}
        autoHideDuration={4000}
        onClose={() => setSnackbar(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar?.severity ?? 'info'} onClose={() => setSnackbar(null)}>
          {snackbar?.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
